"use client";

/**
 * useCredits — fetches the signed-in user's credit balance from FastAPI.
 *
 * Mirrors the CreditPill pattern in components/editor/editor-header.tsx
 * (#336): hits `${API_BASE}/credits/balance` with credentials, reads
 * `credit_balance`, and refreshes whenever:
 *   - the window regains focus (user came back from another tab / top-up flow)
 *   - any code fires the `chitravan:credits-changed` event (watermark debit
 *     #339, ad-hoc audio gen, etc.) so the sidebar stays in sync without polling.
 *
 * Returns `null` until the first fetch resolves so callers can show a
 * loading placeholder.
 */
import { useEffect, useState } from "react";
import { API_BASE } from "@/lib/chitravan-api";

export function useCredits(): number | null {
	const [balance, setBalance] = useState<number | null>(null);

	useEffect(() => {
		let cancelled = false;
		// Bounded retry so a transient first-load failure self-heals instead of
		// leaving the sidebar stuck on "… credits" forever (C4). Only the INITIAL
		// load retries; focus/credits-changed refreshes are single-shot (they
		// keep the last known value on failure, which is correct).
		const fetchBalance = async (retriesLeft = 0) => {
			try {
				const r = await fetch(`${API_BASE}/credits/balance`, {
					credentials: "include",
					cache: "no-store",
				});
				if (!r.ok) throw new Error(`HTTP ${r.status}`);
				const data = (await r.json()) as { credit_balance?: number };
				if (!cancelled && typeof data.credit_balance === "number") {
					setBalance(data.credit_balance);
				}
			} catch {
				// Retry the initial load a few times before giving up. Keeps last
				// known value (null on first load) so callers still show "…".
				if (!cancelled && retriesLeft > 0) {
					setTimeout(() => void fetchBalance(retriesLeft - 1), 2000);
				}
			}
		};
		void fetchBalance(3);
		const onChange = () => void fetchBalance();
		window.addEventListener("chitravan:credits-changed", onChange);
		window.addEventListener("focus", onChange);
		return () => {
			cancelled = true;
			window.removeEventListener("chitravan:credits-changed", onChange);
			window.removeEventListener("focus", onChange);
		};
	}, []);

	return balance;
}
